import { getCampaignConfig, isStepOptional, shouldAutoCompleteEmptyStep } from '../../config/campaigns.config'

export interface StepValidationResult {
  isCompleted: boolean
  isSkipped: boolean
  isEmpty: boolean
  isValid: boolean
  errors: string[]
}

/**
 * Validate step completion with campaign-aware optional step handling
 */
export const validateStepCompletion = (
  campaignId: string,
  stepIndex: number,
  stepData: any
): StepValidationResult => {
  const campaign = getCampaignConfig(campaignId)
  
  if (!campaign) {
    return { 
      isCompleted: false, 
      isSkipped: false, 
      isEmpty: true,
      isValid: false,
      errors: ['Campaign configuration not found']
    }
  }

  const step = campaign.steps[stepIndex]
  const stepId = step?.id

  // Check if step is already marked explicitly
  if (stepData?.isCompleted === true) {
    return { 
      isCompleted: true, 
      isSkipped: false, 
      isEmpty: false,
      isValid: true,
      errors: []
    }
  }

  if (stepData?.isSkipped === true) {
    const isEmpty = hasNoMeaningfulData(stepData)
    return { 
      isCompleted: false, 
      isSkipped: true, 
      isEmpty,
      isValid: true, // Skipped steps are valid
      errors: []
    }
  }

  // Check if step is optional
  const isOptional = isStepOptional(campaignId, stepId)

  // Check if step has meaningful data
  const isEmpty = hasNoMeaningfulData(stepData)

  // Optional step logic
  if (isOptional && shouldAutoCompleteEmptyStep(campaignId, stepIndex)) {
    return { 
      isCompleted: true, 
      isSkipped: false, 
      isEmpty,
      isValid: true,
      errors: []
    }
  }

  // Required step logic
  if (isEmpty) {
    const isRequired = !isOptional
    return { 
      isCompleted: false, 
      isSkipped: true, 
      isEmpty: true,
      isValid: !isRequired, // Invalid if required and empty
      errors: isRequired ? ['This step is required'] : []
    }
  }

  return { 
    isCompleted: true, 
    isSkipped: false, 
    isEmpty: false,
    isValid: true,
    errors: []
  }
}

/**
 * Check if step data is empty/meaningless
 */
const hasNoMeaningfulData = (stepData: any): boolean => {
  if (!stepData) return true

  const meaningfulKeys = Object.keys(stepData).filter(key => 
    !['isCompleted', 'isSkipped', 'campaignId', 'selectedCampaign', 'completedSteps', 'skippedSteps'].includes(key)
  )

  if (meaningfulKeys.length === 0) return true

  return meaningfulKeys.every(key => {
    const value = stepData[key]
    
    if (value === null || value === undefined || value === '') return true
    if (Array.isArray(value) && value.length === 0) return true
    if (typeof value === 'object' && Object.keys(value).length === 0) return true
    
    return false
  })
}

/**
 * Campaign-specific step validation rules
 */
export const validateStepByCampaign = (
  campaignId: string,
  stepIndex: number,
  stepData: any
): StepValidationResult => {
  const campaign = getCampaignConfig(campaignId)
  
  if (!campaign) {
    return { 
      isCompleted: false, 
      isSkipped: false, 
      isEmpty: true,
      isValid: false,
      errors: ['Campaign configuration not found']
    }
  }

  const step = campaign.steps[stepIndex]
  const componentName = step?.component

  // Component-specific validation
  switch (componentName) {
    case 'EmergencyNotes':
      return { 
        isCompleted: true, 
        isSkipped: false, 
        isEmpty: hasNoMeaningfulData(stepData),
        isValid: true,
        errors: []
      }
    
    case 'IdentityPreview':
    case 'Step5_IdentityPreview':
    case 'Submit':
    case 'Step6_Submit':
    case 'Signature':
    case 'Step7_Signature':
    case 'FollowUp':
    case 'Step8_FollowUp':
      return { 
        isCompleted: true, 
        isSkipped: false, 
        isEmpty: false,
        isValid: true,
        errors: []
      }
    
    case 'GamingPlatform':
      return addValidationFlags(validateGamingPlatformStep(stepData), step)
    
    case 'IntakeContact':
    case 'RobloxIntakeContact':
    case 'Step0_IntakeContact':
      return addValidationFlags(validateIntakeStep(stepData), step)
    
    case 'InjuredParty':
    case 'Step1_InjuredParty':
      return addValidationFlags(validateInjuredPartyStep(stepData), step)
    
    default:
      return validateStepCompletion(campaignId, stepIndex, stepData)
  }
}

// Helper to add validation flags to existing results
const addValidationFlags = (
  result: { isCompleted: boolean; isSkipped: boolean; isEmpty: boolean },
  step: any
): StepValidationResult => {
  const isValid = result.isCompleted || result.isSkipped || step?.isOptional
  const errors: string[] = []
  
  if (!isValid && !result.isCompleted && !result.isSkipped) {
    errors.push('Step validation failed')
  }
  
  if (!isValid && result.isEmpty && !step?.isOptional) {
    errors.push('Required fields are missing')
  }
  
  return {
    ...result,
    isValid,
    errors
  }
}

// Helper validation functions
const validateGamingPlatformStep = (stepData: any): { 
  isCompleted: boolean; 
  isSkipped: boolean; 
  isEmpty: boolean 
} => {
  const hasPlatformData = stepData?.selectedPlatforms?.length > 0
  const gamerTagsSkipped = stepData?.gamerTagsSkipped === true

  if (gamerTagsSkipped) {
    return { isCompleted: true, isSkipped: false, isEmpty: false }
  }

  const isCompleted = hasPlatformData
  return { isCompleted, isSkipped: !isCompleted, isEmpty: !hasPlatformData }
}

const validateIntakeStep = (stepData: any): { 
  isCompleted: boolean; 
  isSkipped: boolean; 
  isEmpty: boolean 
} => {
  // ✅ FIXED: Check for INTAKE fields (not injured party fields)
  const hasEssentialInfo = 
    stepData?.intakeFirstName &&
    stepData?.intakeLastName &&
    (stepData?.intakeEmail || stepData?.clientEmail) &&
    stepData?.relationToVictim

  // Additional checks for minor verification
  const hasMinorVerification = stepData?.isMinor !== undefined
  
  // For Roblox: if minor, check for verbal permission
  const robloxMinorChecks = 
    !stepData?.isMinor || 
    (stepData?.verbalPermission && stepData?.relationshipToMinor)

  const isCompleted = hasEssentialInfo && hasMinorVerification && robloxMinorChecks

  return { 
    isCompleted, 
    isSkipped: false,  // ✅ Never mark intake as skipped if user filled it
    isEmpty: !hasEssentialInfo 
  }
}

const validateInjuredPartyStep = (stepData: any): { 
  isCompleted: boolean; 
  isSkipped: boolean; 
  isEmpty: boolean 
} => {
  const hasEssentialInfo = 
    stepData?.injuredPartyFirstName && 
    stepData?.injuredPartyLastName && 
    (stepData?.birthDay || stepData?.injuredPartyDOB)

  return { 
    isCompleted: !!hasEssentialInfo, 
    isSkipped: !hasEssentialInfo, 
    isEmpty: !hasEssentialInfo 
  }
}